/**
 * Diversion Calculator Component
 * Shows breakdown of waste streams by diversion method
 */


import type { WasteStream } from '../../types';
import { formatWeight, formatDiversionMethod } from '../../utils/formatting';

interface DiversionCalculatorProps {
  wasteStreams: WasteStream[];
}

export function DiversionCalculator({ wasteStreams }: DiversionCalculatorProps) {
  // Group waste streams by diversion method
  const byMethod = wasteStreams.reduce((acc, stream) => {
    const method = stream.diversion_method;
    if (!acc[method]) {
      acc[method] = 0;
    }
    acc[method] += stream.estimated_quantity;
    return acc;
  }, {} as Record<string, number>);

  // Define method order and colors
  const methodConfig = [
    { key: 'recycle', color: 'bg-accent-500' },
    { key: 'reuse', color: 'bg-primary-500' },
    { key: 'salvage', color: 'bg-blue-500' },
    { key: 'donate', color: 'bg-purple-500' },
    { key: 'landfill', color: 'bg-neutral-500' },
  ];

  const total = Object.values(byMethod).reduce((sum, val) => sum + val, 0);

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-neutral-900 mb-4">
        Waste Diversion Breakdown
      </h3>

      {wasteStreams.length === 0 ? (
        <p className="text-neutral-600 text-center py-8">
          No waste streams added yet. Add waste streams to see diversion breakdown.
        </p>
      ) : (
        <div className="space-y-4">
          {methodConfig.map(({ key, color }) => {
            const amount = byMethod[key] || 0;
            const percentage = total > 0 ? (amount / total) * 100 : 0;

            if (amount === 0) return null;

            return (
              <div key={key}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-neutral-900">
                    {formatDiversionMethod(key)}
                  </span>
                  <span className="text-sm text-neutral-600">
                    {formatWeight(amount)} ({percentage.toFixed(1)}%)
                  </span>
                </div>
                <div className="w-full bg-neutral-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${color} transition-all duration-300`}
                    style={{ width: `${percentage}%` }}
                    role="progressbar"
                    aria-valuenow={percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              </div>
            );
          })}

          <div className="border-t border-neutral-200 pt-4 mt-4">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-neutral-900">Total Waste</span>
              <span className="font-semibold text-neutral-900">{formatWeight(total)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}